import axios from 'axios';

const isProduction = process.env.NODE_ENV === 'production';

// Determine which base URL to use based on environment
const getBaseUrl = () => {
  // For production, use the configured API URL or default to the production domain
  if (isProduction) {
    return process.env.REACT_APP_API_URL || 'https://tharpeindustries.com/api';
  }
  
  // For development, use the configured API URL or default to localhost
  return process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
};

// Log the base URL for debugging
console.log('API Base URL:', getBaseUrl());

// Create an axios instance with a base URL pointing to our backend
const axiosInstance = axios.create({
  baseURL: getBaseUrl(),
  timeout: isProduction ? 15000 : 10000, // Longer timeout in production for potentially slower connections
  headers: {
    'Content-Type': 'application/json'
  }
});

// Request interceptor to handle authorization
axiosInstance.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('authToken');
    if (token) {
      // Prefer Authorization header with Bearer scheme (more standard)
      config.headers.Authorization = `Bearer ${token}`;
      
      // Only add x-auth-token for backward compatibility
      // This can be removed in a future version when all backend code is updated
      config.headers['x-auth-token'] = token;
    }
    
    // Add security headers to all requests
    config.headers['X-Requested-With'] = 'XMLHttpRequest'; // Helps prevent CSRF
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle common errors
axiosInstance.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    // Handle authentication errors
    if (error.response) {
      switch (error.response.status) {
        case 401: // Unauthorized
          // Clear token and redirect to login
          localStorage.removeItem('authToken');
          // Only redirect if not already on login page
          if (window.location.pathname !== '/login') {
            window.location.href = '/login';
          }
          break;
          
        case 403: // Forbidden
          // User is authenticated but doesn't have permission
          console.error('Access forbidden:', error.response.data);
          // Could redirect to unauthorized page if needed
          // window.location.href = '/unauthorized';
          break;
          
        case 429: // Too Many Requests
          console.error('Rate limit exceeded:', error.response.data);
          // Could show a user-friendly message about rate limiting
          break;
      }
    } else if (error.request) {
      // Request was made but no response received (network error)
      console.error('Network error:', error.request);
    }
    
    return Promise.reject(error);
  }
);

// Create a base API object that maintains compatibility with existing code
const api = {
  get: (url, config) => axiosInstance.get(url, config),
  post: (url, data, config) => axiosInstance.post(url, data, config),
  put: (url, data, config) => axiosInstance.put(url, data, config),
  delete: (url, config) => axiosInstance.delete(url, config),
  patch: (url, data, config) => axiosInstance.patch(url, data, config),
  
  // Authentication API calls
  auth: {
    login: (email, password) => axiosInstance.post('/auth/login', { email, password }),
    register: (userData) => axiosInstance.post('/auth/register', userData),
    getCurrentUser: () => axiosInstance.get('/auth/me'),
    updateUser: (userId, userData) => axiosInstance.put(`/auth/users/${userId}`, userData),
    getUsers: () => axiosInstance.get('/auth/users'),
    getUserById: (userId) => axiosInstance.get(`/auth/users/${userId}`),
    deleteUser: (userId) => axiosInstance.delete(`/auth/users/${userId}`)
  },

  // Organization API calls
  organizations: {
    getAll: () => axiosInstance.get('/organizations'),
    getById: (id) => axiosInstance.get(`/organizations/${id}`),
    create: (orgData) => axiosInstance.post('/organizations', orgData),
    update: (id, orgData) => axiosInstance.put(`/organizations/${id}`, orgData),
    delete: (id) => axiosInstance.delete(`/organizations/${id}`),
    
    // Location endpoints
    getLocations: (orgId) => axiosInstance.get(`/organizations/${orgId}/locations`),
    getLocationById: (orgId, locId) => axiosInstance.get(`/organizations/${orgId}/locations/${locId}`),
    createLocation: (orgId, locData) => axiosInstance.post(`/organizations/${orgId}/locations`, locData),
    updateLocation: (orgId, locId, locData) => axiosInstance.put(`/organizations/${orgId}/locations/${locId}`, locData),
    deleteLocation: (orgId, locId) => axiosInstance.delete(`/organizations/${orgId}/locations/${locId}`)
  },

  // Service API calls
  services: {
    getAll: () => axiosInstance.get('/services'),
    getById: (id) => axiosInstance.get(`/services/${id}`),
    create: (serviceData) => axiosInstance.post('/services', serviceData),
    update: (id, serviceData) => axiosInstance.put(`/services/${id}`, serviceData),
    delete: (id) => axiosInstance.delete(`/services/${id}`)
  },

  // Service Assignment API calls
  serviceAssignments: {
    getAll: () => axiosInstance.get('/service-assignments'),
    getByLocation: (locationId) => axiosInstance.get(`/service-assignments/location/${locationId}`),
    getById: (id) => axiosInstance.get(`/service-assignments/${id}`),
    create: (assignmentData) => axiosInstance.post('/service-assignments', assignmentData),
    update: (id, assignmentData) => axiosInstance.put(`/service-assignments/${id}`, assignmentData),
    delete: (id) => axiosInstance.delete(`/service-assignments/${id}`)
  },

  // Invoice API calls
  invoices: {
    getAll: () => axiosInstance.get('/invoices'),
    getByOrganization: (orgId) => axiosInstance.get(`/invoices/organization/${orgId}`),
    getById: (id) => axiosInstance.get(`/invoices/${id}`),
    create: (invoiceData) => axiosInstance.post('/invoices', invoiceData),
    update: (id, invoiceData) => axiosInstance.put(`/invoices/${id}`, invoiceData),
    delete: (id) => axiosInstance.delete(`/invoices/${id}`),
    generate: (generateData) => axiosInstance.post('/invoices/generate', generateData)
  },

  // Payment API calls
  payments: {
    getAll: () => axiosInstance.get('/payments'),
    getByOrganization: (orgId) => axiosInstance.get(`/payments/organization/${orgId}`),
    getByInvoice: (invoiceId) => axiosInstance.get(`/payments/invoice/${invoiceId}`),
    getById: (id) => axiosInstance.get(`/payments/${id}`),
    create: (paymentData) => axiosInstance.post('/payments', paymentData),
    update: (id, paymentData) => axiosInstance.put(`/payments/${id}`, paymentData),
    processRefund: (id, refundData) => axiosInstance.post(`/payments/${id}/refund`, refundData)
  },

  // Webhook API calls (admin only)
  webhooks: {
    test: (testData) => axiosInstance.post('/webhooks/test', testData)
  }
};

export default api;
